import {
  Avatar,
  Button,
  Card,
  Col,
  Divider,
  EditButton,
  Icons,
  ListButton,
  Popconfirm,
  Row,
  Show,
  Space,
  Table,
  TableProps,
  Tooltip,
  Typography,
  notification,
} from '@pankod/refine-antd';
import { useCan, useNavigation, useOne } from '@pankod/refine-core';
import { useParams } from '@pankod/refine-react-router-v6';
import { PaymentLogsTable, ProductFooter } from 'components';
import { EStatus } from 'enums';
import { IPurchase } from 'interfaces';
import { deletePurchase } from 'services/purchase';
import { formatCurrency } from 'utilities/shared';
import styles from './style.module.css';
import { useState } from 'react';
import CustomTableWithPagination from 'components/table/custom-table';

const { Title, Text } = Typography;

export const PurchaseShow = () => {
  const { list } = useNavigation();
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const { id } = useParams();
  const { data, isLoading } = useOne<IPurchase>({
    resource: 'purchaseCopy',
    id: id || '',
    metaData: {
      fields: [
        'id',
        'purchaseAddress',
        'note',
        'purchaseStatus',
        'vatAmount',
        'shipmentCost',
        'discountCost',
        'subTotal',
        {
          supplierPoc: ['id', 'name'],
        },
        {
          warehouse: ['id'],
        },
        'totalAmount',
        {
          supplier: ['id', 'name'],
        },
        {
          receipts: [
            {
              attachment: ['uid', 'type', 'url', 'name'],
            },
          ],
        },
        {
          purchaseProductsCopy: [
            'id',
            'quantity',
            'unitPrice',
            'subTotal',
            {
              productVariant: [
                'id',
                'name',
                'sku',
                {
                  productCopy: ['name'],
                },
              ],
            },
          ],
        },
      ],
    },
  });

  const { data: canAccessDelete } = useCan({
    resource: 'purchases',
    action: 'delete',
    params: { field: 'id' },
  });

  const purchase = data?.data;
  const note = purchase?.note;
  const supplierId = purchase?.supplier?.id;
  const supplierPoc = purchase?.supplierPoc;
  const totalAmount = purchase?.totalAmount ?? 0;
  const subTotal = purchase?.subTotal ?? 0;
  const vatPercentage = purchase?.vatAmount ?? 0;
  const vatAmount = Number((subTotal * (vatPercentage / 100)).toFixed(2));
  const shipmentCost = purchase?.shipmentCost ?? 0;
  const discountCost = purchase?.discountCost ?? 0;
  const netTotal = purchase?.totalAmount ?? 0;
  const purchases = purchase?.purchaseProductsCopy.map((purchase) => ({
    id: purchase.id,
    sku: purchase.productVariant.sku,
    variantName: purchase.productVariant.name,
    productName: purchase.productVariant.productCopy.name,
    quantity: purchase.quantity,
    unitPrice: purchase.unitPrice,
    subTotal: formatCurrency(purchase.subTotal),
  }));
  const receiptsData = purchase?.receipts;

  const isCompleted = purchase?.purchaseStatus === EStatus.COMPLETED;

  const handleDelete = async (id?: string) => {
    if (!id) return;

    try {
      await deletePurchase(id);
      notification.success({
        message: 'Success',
        description: 'Successfully deleted a purchase',
      });
      list('purchases');
    } catch (err) {
      notification.error({
        message: 'Failed',
        description: 'Failed to delete purchase',
      });
    }
  };

  const pageHeader = !isLoading ? (
    <>
      <ListButton />
      <EditButton hidden />
      <Popconfirm
        title="Are you sure?"
        okText="Delete"
        onConfirm={() => handleDelete(id)}
        disabled={!canAccessDelete?.can}
      >
        <Button
          size="middle"
          danger
          icon={<Icons.DeleteOutlined />}
          disabled={!canAccessDelete?.can}
          hidden={isCompleted}
        >
          Delete
        </Button>
      </Popconfirm>
    </>
  ) : null;

  const receipts = () => {
    if (!receiptsData || !receiptsData.length) {
      return <Text style={{ color: 'rgba(0 , 0, 0, 0.45)' }}>No Receipts</Text>;
    }

    const receipts = [];
    for (let receipt of receiptsData) {
      if (receipt.attachment === null) {
        continue;
      }

      const { uid, type, url, name } = receipt.attachment;
      receipts.push(<FileCard key={uid} type={type} url={url} title={name} />);
    }
    return receipts;
  };

  const footer = () => {
    return (
      <ProductFooter
        subTotal={subTotal || 0}
        vatAmount={vatAmount}
        vatPercentage={vatPercentage || 0}
        shipmentCost={shipmentCost || 0}
        netTotal={netTotal || 0}
        discountCost={discountCost || 0}
      />
    );
  };

  const onTableChange: TableProps<any>['onChange'] = async (
    pagination,
    filters,
    _sorter,
    _extra
  ) => {
    const page = pagination.current ?? 1;
    const limit = pagination.pageSize ?? 10;
    setPage(page);
    setLimit(limit);
    // setStatuses(() => {
    //   const statuses = (filters?.salesStatus ?? []) as EStatus[];
    //   localStorage.setItem('salesStatus', statuses.join(','));
    //   return statuses;
    // });
    // setSelectedWarehouse(() => {
    //   const warehouse = (filters['warehouse.name'] ?? []) as string[];
    //   localStorage.setItem('salesWarehouse', warehouse.join(','));
    //   return warehouse;
    // });
  };

  const onPageChange: any = async (page: number, pageSize: number) => {
    setPage(page);
    setLimit(pageSize);
  };

  // Function to calculate the current page data
  const getCurrentPageData = () => {
    const startIndex = (page - 1) * limit;
    const endIndex = startIndex + limit;
    return purchases?.slice(startIndex, endIndex);
  };

  return (
    <>
      <Show isLoading={isLoading} headerProps={{ extra: pageHeader }}>
        {/* Purchase Info */}
        <Row gutter={[32, 32]}>
          <Col xs={24} md={24} lg={7}>
            <Card title="Receipts">
              <Row justify="space-around" align="middle">
                {receipts()}
              </Row>
            </Card>
          </Col>

          <Col xs={24} md={24} lg={17}>
            <Card title="Purchase Information">
              <Title level={5}>Supplier Name</Title>
              <Text>{purchase?.supplier.name}</Text>
              <Title level={5}>Point Of Contact</Title>
              <Text>{supplierPoc?.name}</Text>
              <Title level={5}>Shipping Address</Title>
              <Text>{purchase?.purchaseAddress}</Text>
              <Title level={5}>Note</Title>
              <Text>{note}</Text>
            </Card>
          </Col>
        </Row>
      </Show>

      <Divider />

      {/* Products Table */}
      <Row wrap={false}>
        <Col flex="auto">
          <CustomTableWithPagination
            total={purchases?.length || 0}
            page={page}
            limit={limit}
            onPageChange={onPageChange}
          >
            <Table
              // dataSource={purchases}
              dataSource={getCurrentPageData()} // Pass sliced data
              rowKey="id"
              scroll={{ x: true }}
              footer={(_data) => footer()}
              pagination={false}
              onChange={onTableChange}
            >
              <Table.Column dataIndex="sku" title="SKU" />
              <Table.Column dataIndex="variantName" title="Variant Name" />
              <Table.Column dataIndex="productName" title="Product Name" />
              <Table.Column dataIndex="quantity" title="Quantity" />
              <Table.Column dataIndex="unitPrice" title="Unit Price" />
              <Table.Column dataIndex="subTotal" title="Total" />
            </Table>
          </CustomTableWithPagination>
        </Col>
      </Row>

      <Divider />

      {/* Payments Table */}
      <Row wrap={false}>
        <Col flex="auto">
          {purchase?.id && supplierId && (
            <PaymentLogsTable
              resource="purchase"
              resourceId={purchase.id}
              paymentBy="supplier"
              paymentById={supplierId}
              totalPayableAmount={totalAmount}
            />
          )}
        </Col>
      </Row>
    </>
  );
};

type FileCardProps = {
  type: string;
  url: string;
  title: string;
};

const FileCard: React.FC<FileCardProps> = (props) => {
  const receiptName = props.title
    .substring(0, props.title.lastIndexOf('.'))
    .substring(0, 10)
    .trim();
  const extension = props.title.substring(props.title.lastIndexOf('.') + 1);

  const download = async (url: string, filename: string) => {
    const resp = await fetch(url);
    const blob = await resp.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = downloadUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(downloadUrl);
  };

  const icon = props.type.includes('pdf') ? <Icons.FilePdfOutlined /> : <Icons.FileImageOutlined />;

  return (
    <div className={styles['file-card']}>
      <Space className={styles['file-card__wrapper']}>
        <a download={props.title} href={props.url} target="_blank" rel="noreferrer">
          <Tooltip title="View" placement="top">
            <Avatar size="small" shape="square" icon={icon} />
          </Tooltip>
        </a>

        <Text>{`${receiptName}.${extension}`}</Text>

        <Tooltip title="Download" placement="top">
          <Button
            size="small"
            icon={<Icons.DownCircleOutlined />}
            onClick={async () => {
              try {
                await download(props.url, props.title);
              } catch (_) {
                notification.error({
                  message: 'Failed',
                  description: `Failed to download ${props.title}`,
                });
              }
            }}
          />
        </Tooltip>
      </Space>
    </div>
  );
};
